import { createContext, useContext, useMemo, useState } from 'react';

import type { RotationOrientation, RotationPosture } from '@agavi/blocks';

/**
 * ONE device, for the whole task.
 *
 * Every sensor block used to carry its own simulator: its own Device/Simulate switch, its own
 * scenario, its own manual fields. That is right in the EDITOR, where a teacher is testing one
 * block against its own answer in isolation, and wrong in Preview, where it models something that
 * cannot happen — a phone does not send one position to one block and a different position to
 * another. Two GPS blocks with different targets could both read "Exact", which is exactly the
 * thing a teacher needs to find out is impossible.
 *
 * So Preview holds one state and the blocks read from it. `null` outside Preview, which is what
 * keeps the editor's per-block simulators untouched: a block that finds no provider above it falls
 * back to its own state and behaves exactly as it always has.
 *
 * POSITION AND HEADING TRAVEL TOGETHER, and not only because a phone has both. `computeTrueHeading`
 * takes a latitude and longitude to work out magnetic declination, so a compass block set to true
 * north consumes a position whether or not the task has a GPS block in it. Splitting them would
 * leave that correction with nothing to correct against.
 */

/**
 * The gesture the simulated phone will perform — a PLAY, set up in advance and run on Calibrate.
 *
 * Not a live attitude, which is what this was. Live meant the panel had to stay open and out of the
 * way while the block was worked, which is impossible on a phone and awkward on a laptop: the panel
 * covers the task. Setting the whole gesture up front and running it in one press removes the
 * need to see both at once, so the panel can sit where a modal belongs.
 *
 * It is still an attitude rather than an angle: the block derives its reading by running this
 * through the same tracker a real accelerometer feeds. See `rotation-simulation.ts` for why there are
 * exactly two postures and why a posture and its pivot cannot be chosen separately.
 */
export interface SimulatedAttitude {
  /** Lying on the thing being measured, or stood up facing you. The only one of the three that
   * changes what the block reports: flat is a tilt, upright is a sweep. */
  posture: RotationPosture;
  /** Which way round, within a posture. A picture, since the sweep began measuring from its own
   * calibration attitude. */
  orientation: RotationOrientation;
  /**
   * Whether the phone turns about a corner or about its middle.
   *
   * Purely a drawing, and unavoidably so: gravity is a direction, so it sees the axis a phone turns
   * about but never where along that axis the hinge sits. Offered because it is what a teacher's
   * apparatus actually looks like, and because seeing the reading NOT change is worth something.
   */
  centre: 'pivot' | 'rotate';
  /**
   * Where the phone is when Calibrate is pressed.
   *
   * Zero — flat, or the rod horizontal — is the procedure a student is given, and the default. It
   * is settable because calibrating somewhere else is the case the posture guess and the
   * near-vertical trade both turn on, and until now there was no way to reach it.
   */
  startDegrees: number;
  /** Where the phone ends up, and therefore what the block should report. */
  endDegrees: number;
  /** The sample stream keeps breaking, the way a phone that sleeps mid-gesture does. */
  interrupted: boolean;
}

export interface SimulatedPosition {
  lat: number;
  lng: number;
  /** Metres. GPS gates on this before it ever measures distance. */
  accuracy: number;
}

export interface SimulatedHeading {
  degrees: number;
  /** Degrees. Compass gates on this the way GPS gates on position accuracy. */
  accuracy: number;
}

/**
 * How well the device is receiving, as one setting rather than a scenario per sensor.
 *
 * A condition is not a position, so it does not belong on the map: dragging a pin says where you
 * are, and none of the dragging in the world says the signal dropped. These are the faults the
 * per-block scenario lists carried — Low Accuracy, Network-Based, No Reading — lifted to the device
 * they actually describe.
 */
export type SimulatedSignal = 'good' | 'poor' | 'none';

export interface SensorSimulatorState {
  position: SimulatedPosition;
  heading: SimulatedHeading;
  attitude: SimulatedAttitude;
  signal: SimulatedSignal;
  /**
   * Reading the REAL sensors instead of the simulated ones.
   *
   * Kept as a task-level choice for the same reason as everything else here: one device is either
   * being read or it is not. Switching to it stays a deliberate gesture, which is also what iOS
   * requires before it will grant motion or orientation permission.
   */
  useDevice: boolean;
}

export interface SensorSimulatorValue extends SensorSimulatorState {
  setPosition: (next: SimulatedPosition) => void;
  setHeading: (next: SimulatedHeading) => void;
  setAttitude: (next: SimulatedAttitude) => void;
  setSignal: (next: SimulatedSignal) => void;
  setUseDevice: (next: boolean) => void;
}

/** Metres. What a healthy fix reports, and the floor every GPS accuracy cutoff sits above. */
export const SIM_GOOD_ACCURACY = 5;
/** Metres. Bad enough to trip a typical accuracy cutoff without being absurd. */
export const SIM_POOR_ACCURACY = 65;
/** Degrees. The compass equivalents. */
export const SIM_GOOD_HEADING_ACCURACY = 5;
export const SIM_POOR_HEADING_ACCURACY = 40;

/**
 * Where the pin starts before a teacher has touched anything.
 *
 * Deliberately NOT (0, 0), which is in the Atlantic and would put every task's pin thousands of
 * kilometres from its target on first open. The provider replaces this with the first authored
 * target it can find; this only survives for a task whose sensor blocks have no target set yet,
 * where any point is as wrong as any other.
 */
const NOWHERE: SimulatedPosition = { lat: 0, lng: 0, accuracy: SIM_GOOD_ACCURACY };

const SensorSimulatorContext = createContext<SensorSimulatorValue | null>(null);

/**
 * Read the task's one device, or `null` when there isn't one.
 *
 * Null is the editor, and it is a supported answer rather than an error: a block outside Preview
 * owns its own simulator and must keep doing so. Every caller therefore reads this as "is a shared
 * device driving me right now", never as "give me the device".
 */
export function useSensorSimulator(): SensorSimulatorValue | null {
  return useContext(SensorSimulatorContext);
}

export function SensorSimulatorProvider({ initialPosition, children }: {
  /** The first authored target in the task, so the pin opens somewhere meaningful. */
  initialPosition?: { lat: number; lng: number };
  children: React.ReactNode;
}) {
  const [position, setPosition] = useState<SimulatedPosition>(
    () => (initialPosition
      ? { lat: initialPosition.lat, lng: initialPosition.lng, accuracy: SIM_GOOD_ACCURACY }
      : NOWHERE),
  );
  const [heading, setHeading] = useState<SimulatedHeading>(
    () => ({ degrees: 0, accuracy: SIM_GOOD_HEADING_ACCURACY }),
  );
  // Flat and unturned: the arrangement a student is told to start from, and the one whose zero is
  // the easiest to actually achieve against a real surface.
  const [attitude, setAttitude] = useState<SimulatedAttitude>(
    () => ({ posture: 'flat', orientation: 'portrait', centre: 'pivot', startDegrees: 0, endDegrees: 0, interrupted: false }),
  );
  const [signal, setSignal] = useState<SimulatedSignal>('good');
  const [useDevice, setUseDevice] = useState(false);

  const value = useMemo<SensorSimulatorValue>(() => ({
    position, heading, attitude, signal, useDevice,
    setPosition, setHeading, setAttitude, setSignal, setUseDevice,
  }), [position, heading, attitude, signal, useDevice]);

  return <SensorSimulatorContext.Provider value={value}>{children}</SensorSimulatorContext.Provider>;
}

/**
 * The accuracy the current signal implies, per channel.
 *
 * Held here rather than at the two call sites so the two channels cannot drift about what "poor"
 * means, and so a third channel later has one place to join.
 */
export function accuracyForSignal(signal: SimulatedSignal): { position: number; heading: number } {
  return signal === 'good'
    ? { position: SIM_GOOD_ACCURACY, heading: SIM_GOOD_HEADING_ACCURACY }
    : { position: SIM_POOR_ACCURACY, heading: SIM_POOR_HEADING_ACCURACY };
}

/**
 * What a poor signal means to an accelerometer: noise, as a fraction of g.
 *
 * The third channel joining `accuracyForSignal`, and the reason the angle block needs no fault list
 * of its own. Its old "No Reading" scenario is `signal: none` — the device reporting nothing — and
 * its unreachable "latched late" case is what this figure produces, since the 6-degree latch
 * threshold exists precisely to survive noise.
 *
 * ZERO on a good signal, which was not the first answer and should have been. A realistic phone at
 * rest carries about 0.02g, and modelling that put roughly a degree of error on every reading — so
 * the dial said 45 and the block said 44.79, and the mismatch read as a bug in the simulator rather
 * than as honesty about hardware. It was also inconsistent with the two channels beside it:
 * `accuracyForSignal` REPORTS five metres on a good fix, it does not move the pin off where the
 * teacher put it. A good signal means the instrument agrees with the setup on all three now.
 */
export function noiseForSignal(signal: SimulatedSignal): number {
  return signal === 'good' ? 0 : 0.08;
}
